import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { MessageSquare, Send, Plus, Trash2, Archive, ArchiveRestore, Loader2, Briefcase, Hash } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Phase F.5 Batch 16 — Internal Team Chat. */
export default function TeamChat() {
  const { user } = useAuth();
  const isAdmin = user?.account_type === "internal" || user?.role === "office_manager" || user?.is_platform_admin;
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const listRef = useRef(null);

  const loadChannels = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/channels`, { params: { include_archived: showArchived } });
      setChannels(r.data?.channels || []);
      if (!activeId && r.data?.channels?.length) setActiveId(r.data.channels[0].id);
    } catch (e) { /* */ }
  }, [showArchived, activeId]);

  const loadMessages = useCallback(async () => {
    if (!activeId) { setMessages([]); return; }
    try {
      const r = await axios.get(`${API}/chat/channels/${activeId}/messages`);
      setMessages(r.data?.messages || []);
      // mark read
      await axios.post(`${API}/chat/channels/${activeId}/read`);
      // refresh sidebar unread counters
      loadChannels();
    } catch (e) { /* may be 403 if non-member */ }
  }, [activeId, loadChannels]);

  useEffect(() => { loadChannels(); }, [loadChannels]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Poll new messages every 6s
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(loadMessages, 6000);
    return () => clearInterval(t);
  }, [activeId, loadMessages]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!body.trim() || !activeId) return;
    try {
      await axios.post(`${API}/chat/channels/${activeId}/messages`, { body });
      setBody(""); loadMessages();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
  };

  const createChannel = async () => {
    if (!newName.trim()) return;
    try {
      const r = await axios.post(`${API}/chat/channels`, { name: newName.trim() });
      toast.success("Channel created");
      setNewName(""); setCreating(false); setActiveId(r.data.id); loadChannels();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const archive = async (id) => {
    try { await axios.post(`${API}/chat/channels/${id}/archive`); toast.success("Archived"); loadChannels(); }
    catch (e) { toast.error("Failed"); }
  };
  const unarchive = async (id) => {
    try { await axios.post(`${API}/chat/channels/${id}/unarchive`); toast.success("Unarchived"); loadChannels(); }
    catch (e) { toast.error("Failed"); }
  };

  const deleteMsg = async (msgId) => {
    if (!window.confirm("Delete this message?")) return;
    try { await axios.delete(`${API}/chat/channels/${activeId}/messages/${msgId}`); loadMessages(); }
    catch (e) { toast.error("Failed"); }
  };

  const active = channels.find((c) => c.id === activeId);

  return (
    <div className="p-4 space-y-3" data-testid="team-chat-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MessageSquare className="h-6 w-6" /> Team Chat</h1>
          <p className="text-sm text-muted-foreground">Internal group chats. Auto-job channels stay attached to a Schedule.</p>
        </div>
        <div className="flex gap-1">
          <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived(!showArchived)} data-testid="tc-toggle-archived">
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          {isAdmin && (
            <Button onClick={() => setCreating(true)} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="tc-new-channel-btn"><Plus className="h-4 w-4 mr-1" /> New channel</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 min-h-[60vh]">
        {/* Channel list */}
        <Card className="md:col-span-1">
          <CardHeader className="py-3"><CardTitle className="text-base">Channels</CardTitle></CardHeader>
          <CardContent className="space-y-1 p-2">
            {channels.length === 0 ? (
              <p className="text-xs text-muted-foreground p-2" data-testid="tc-channels-empty">No channels yet.</p>
            ) : (
              channels.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`w-full text-left p-2 rounded hover:bg-muted flex items-center justify-between gap-2 ${activeId === c.id ? "bg-muted" : ""}`}
                  data-testid={`tc-channel-${c.id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {c.kind === "job" ? <Briefcase className="h-3.5 w-3.5 shrink-0 text-blue-600" /> : <Hash className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{c.name}</span>
                  </span>
                  {c.unread > 0 && <Badge variant="destructive" className="h-5 px-1.5">{c.unread}</Badge>}
                </button>
              ))
            )}
          </CardContent>
        </Card>

        {/* Active channel */}
        <Card className="md:col-span-3 flex flex-col">
          <CardHeader className="py-3 border-b">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                {active ? (active.kind === "job" ? <Briefcase className="h-4 w-4 text-blue-600" /> : <Hash className="h-4 w-4" />) : null}
                {active ? active.name : "Select a channel"}
                {active?.is_archived && <Badge variant="secondary">archived</Badge>}
              </CardTitle>
              {active && isAdmin && (
                <div className="flex gap-1">
                  {active.is_archived
                    ? <Button size="sm" variant="outline" onClick={() => unarchive(active.id)} data-testid="tc-unarchive-btn"><ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Unarchive</Button>
                    : <Button size="sm" variant="outline" onClick={() => archive(active.id)} data-testid="tc-archive-btn"><Archive className="h-3.5 w-3.5 mr-1" /> Archive</Button>}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 p-0">
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="tc-messages">
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center mt-12" data-testid="tc-messages-empty">No messages yet — say hi.</p>
              ) : (
                messages.map((m) => {
                  const mine = m.sender_user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`tc-msg-${m.id}`}>
                      <div className={`max-w-[75%] p-2 rounded-lg ${mine ? "bg-emerald-600 text-white" : "bg-muted"} ${m.is_deleted ? "italic opacity-60" : ""}`}>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.is_deleted ? "(message deleted)" : m.body}</p>
                        <div className="text-[10px] mt-1 opacity-70 flex items-center gap-2">
                          <span>{new Date(m.created_at).toLocaleString()}</span>
                          {mine && !m.is_deleted && (
                            <button onClick={() => deleteMsg(m.id)} className="hover:underline" data-testid={`tc-msg-del-${m.id}`}>
                              <Trash2 className="h-3 w-3 inline" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {active && !active.is_archived && (
              <form onSubmit={send} className="border-t p-2 flex gap-2" data-testid="tc-compose-form">
                <Textarea
                  rows={1}
                  placeholder="Write a message…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  className="resize-none"
                  data-testid="tc-compose-input"
                />
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="tc-send-btn"><Send className="h-4 w-4" /></Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={creating} onOpenChange={(o) => { if (!o) { setCreating(false); setNewName(""); } }}>
        <DialogContent data-testid="tc-create-dialog">
          <DialogHeader>
            <DialogTitle>New channel</DialogTitle>
            <DialogDescription>Pick a name. You can add members from the channel view after creation.</DialogDescription>
          </DialogHeader>
          <Input placeholder="e.g. Crew A, Site B Updates" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="tc-new-channel-name" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setNewName(""); }}>Cancel</Button>
            <Button onClick={createChannel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tc-create-confirm-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
